import React, { useState, useRef, useEffect } from "react";
import { Button, Typography, Box, CircularProgress } from "@mui/material";
import { FaUpload } from "react-icons/fa";
import DocumentDataPopup from "./DocumentDataPopup";
import { toast } from "react-toastify";

const SalesOrderUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const fileInputRef = useRef(null);

  {/* ✅ ONLY NECESSARY ADDITION */}
  useEffect(() => {
    window.__UPLOAD_PANEL_ACTIVE = true;
    return () => {
      window.__UPLOAD_PANEL_ACTIVE = false;
    };
  }, []);

  // ------------ ORIGINAL CODE BELOW (unchanged) -------------
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    } else {
      alert("Please select a valid Purchase Order PDF file.");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert("Please select a file to upload.");
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append("so_pdf", selectedFile);

    try {
      const response = await fetch("https://cd9d-205-254-163-120.ngrok-free.app/process-so", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.message || "Failed to extract Sales Order data from PO.");
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      setPopupData(data);
      setIsPopupOpen(true);
    } catch (error) {
      toast.error(`An error occurred: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePopupSubmit = async () => {
    const soData = Array.isArray(popupData) ? popupData[0] : popupData;

    if (!soData || !soData.item_details || soData.item_details.length === 0) {
      toast.error("No Sales Order data available to submit.");
      return;
    }

    const items = soData.item_details;

    // New ZSALES_ODATA2_SRV schema — no "d" wrapper, renamed fields
    const payload = {
      SaleDocType: "OR",
      SalesOrg: "TS01",
      DisChannel: "10",
      Divisioin: "LT",
      SalGrp: "",
      SalOffice: "",
      ZAI_SALES_ITEMSet: [],
    };

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      payload.ZAI_SALES_ITEMSet.push({
        SalesDocument: "",
        ItemNumber: String((i + 1) * 10).padStart(6, "0"),
        Material: item.Material,
        Plant: "TS02",
        StoreLoc: "",
        Targetqty: "0000",
        Targetqu: item.TargetQu,
        Shippoint: "2T01",
        Partnrole: "AG",
        Partnnumber: "9000002",
        Reqqty: item.ReqQty,
      });
    }

    const finalData = {
      url: "https://202.83.16.193:8001/sap/opu/odata/sap/ZSALES_ODATA2_SRV/ZAI_SALES_HEADERSet?sap-client=100",
      payload,
    };

    try {
      const response = await fetch("https://cd9d-205-254-163-120.ngrok-free.app/submit-so", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(finalData),
      });

      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        result = { raw: text };
      }

      if (response.ok && result[0]?.status_code === 201) {
        toast.success(
          ` Sales Order created successfully — Document No: ${result[0].document_number}`
        );
        handlePopupClose();
      } else if (result.status_code === 201) {
        toast.success(
          ` Sales Order created successfully — Document No: ${result.document_number}`
        );
        handlePopupClose();
      } else {
        toast.error(result?.message || "Failed to create Sales Order.");
      }
    } catch (error) {
      toast.error(`Submission failed: ${error.message}`);
    }
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupData(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  return (
    <Box textAlign="center" sx={{ p: 1.5, mt: 0.5, mb: 0.5 }}>
      <Typography variant="h5" gutterBottom>
        Upload Purchase Order (to Create Sales Order)
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" mb={3}>
        Upload a Purchase Order PDF to extract data and auto-create a Sales Order in SAP.
      </Typography>

      <Button component="label" variant="contained" startIcon={<FaUpload />} sx={{ mb: 2 }}>
        {selectedFile ? selectedFile.name : "Select Purchase Order PDF"}
        <input
          type="file"
          hidden
          accept="application/pdf"
          onChange={handleFileChange}
          ref={fileInputRef}
        />
      </Button>

      {selectedFile && (
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={isLoading}
            sx={{ minWidth: "160px" }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Process Purchase Order"
            )}
          </Button>
        </Box>
      )}

      {popupData && (
        <DocumentDataPopup
          open={isPopupOpen}
          onClose={handlePopupClose}
          data={popupData}
          onSubmit={handlePopupSubmit}
          documentType="SalesOrder"
        />
      )}
    </Box>
  );
};

export default SalesOrderUpload;
